// The lead source matrix (decision D8; public.dashboard_source_matrix, rnrinfra migration 20261006171641_source_matrix):
// leads by where they came from (Google Ads, Meta Ads, other paid, Google Business Profile, website, not tracked) and
// how they made contact. Each lead is counted once, by its first interaction. Admins and super admins only.

export type MatrixMethod = 'form_fill' | 'phone_call' | 'email_flow' | 'text_popup';
export type MatrixChannel = 'paid' | 'organic' | 'none';
export type MatrixRow = { key: string; channel: MatrixChannel; label: string; total: number; other: number } & Record<MatrixMethod, number>;
export type SourceMatrix = {
  from_date: string; to_date: string; days: number; agency_id: string | null;
  /** live is false for a flow that is not running yet (email and text pop-up, 6 Oct): its column stays empty. */
  methods: { key: MatrixMethod; label: string; live: boolean }[];
  rows: MatrixRow[]; total: number; paid: number | null; organic: number | null;
};

const channelLabels: Record<MatrixChannel, string> = { paid: 'Paid', organic: 'Organic', none: 'No source' };

/** The rows under Paid, Organic and No source, in the database's order, each group with its total. Empty groups drop out. */
export function matrixGroups(matrix: SourceMatrix) {
  return (['paid', 'organic', 'none'] as const)
    .map(channel => {
      const rows = matrix.rows.filter(r => r.channel === channel);
      return { channel, label: channelLabels[channel], rows, total: rows.reduce((n, r) => n + r.total, 0) };
    })
    .filter(g => g.rows.length > 0);
}

/** A whole-number percentage of the total; 0 when there are no leads. */
export const matrixShare = (n: number, total: number) => (total > 0 ? Math.round((100 * n) / total) : 0);

/** The method columns with their totals; "Other" joins only when some lead has another contact method. */
export function matrixColumns(matrix: SourceMatrix) {
  const columns = matrix.methods.map(m => ({ ...m, total: matrix.rows.reduce((n, r) => n + (r[m.key] ?? 0), 0) }));
  const other = matrix.rows.reduce((n, r) => n + (r.other ?? 0), 0);
  return other > 0 ? [...columns, { key: 'other' as const, label: 'Other', live: true, total: other }] : columns;
}
